import { Link } from 'react-router-dom';
import LandingNav from '../components/LandingNav';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';

const BASE_URL = 'https://momentsent.com';
const PATH = '/what-to-write-in-a-birthday-card';

/**
 * Message-ideas guide: /what-to-write-in-a-birthday-card
 *
 * A genuinely useful, citable resource (GEO play): answer-first intro, then
 * dozens of ready-to-use birthday messages grouped by tone and relationship.
 * "What to write in a ___ card" content is highly quotable by AI assistants
 * and ranks for a large family of long-tail searches. Cross-links to
 * /birthday-cards and /signup. Emits Article + FAQPage + BreadcrumbList JSON-LD.
 */

interface MessageGroup {
  heading: string;
  intro:   string;
  messages: string[];
}

const GROUPS: MessageGroup[] = [
  {
    heading: 'Heartfelt and sentimental',
    intro:   'When you want the card to carry real warmth, lead with how much the person means to you.',
    messages: [
      'Happy birthday to someone who makes the world brighter just by being in it. I am so grateful for you.',
      'Another year of you is another year of luck for everyone who loves you. Happy birthday.',
      'On your birthday I just want you to know how much you are loved, today and every day.',
      'You deserve every good thing this year has in store. Happy birthday, with all my love.',
      'Thinking of you on your special day and feeling lucky to have you in my life. Happy birthday.',
      'Wishing you a year as wonderful and kind as you are. Happy birthday.',
    ],
  },
  {
    heading: 'Short and simple',
    intro:   'Sometimes a few warm words are all you need. These work for anyone.',
    messages: [
      'Happy birthday! Hope your day is as special as you are.',
      'Wishing you a wonderful birthday and a year full of good things.',
      'Have the happiest of birthdays. You deserve it.',
      'Cheers to you today. Happy birthday!',
      'Hope this year brings you everything you have been hoping for. Happy birthday.',
      'Sending you a big birthday hug and all my best wishes.',
    ],
  },
  {
    heading: 'Funny and lighthearted',
    intro:   'A little humor makes a card memorable. Keep it kind and keep it fun.',
    messages: [
      'Happy birthday! You are not getting older, you are just becoming a classic.',
      'Another year wiser, and still putting up with me. That is impressive. Happy birthday!',
      'Happy birthday! Let us celebrate the fact that you have survived another lap around the sun.',
      'You and I both know age is just a number, and yours is starting to get pretty high. Happy birthday!',
      'Hope your birthday is as awesome as you think you are. Just kidding, you really are awesome.',
      'Happy birthday! Eat the cake, skip the diet, and enjoy every minute.',
    ],
  },
  {
    heading: 'For a partner or spouse',
    intro:   'Make it personal and a little romantic. This is the card they will keep.',
    messages: [
      'Happy birthday to the love of my life. Every year with you is my favorite year.',
      'You make ordinary days feel special, so I cannot wait to make your birthday unforgettable.',
      'Growing older is easy when I get to do it next to you. Happy birthday, my love.',
      'Of all the gifts in my life, you are the best one. Happy birthday.',
      'Here is to another year of us. I love you more every day. Happy birthday.',
    ],
  },
  {
    heading: 'For a parent',
    intro:   'A birthday is a chance to thank a parent for everything they have given you.',
    messages: [
      'Happy birthday to the person who taught me almost everything I know. I love you.',
      'Thank you for a lifetime of love and patience. Wishing you the happiest of birthdays.',
      'No matter how old I get, I will always look up to you. Happy birthday.',
      'You gave me so much. Today I just want to give you a day full of joy. Happy birthday.',
      'Happy birthday to the heart of our family. We love you more than words can say.',
    ],
  },
  {
    heading: 'For a friend',
    intro:   'Celebrate the friendship along with the birthday.',
    messages: [
      'Happy birthday to a friend who feels more like family. So glad I have you.',
      'Here is to another year of inside jokes, good times, and being there for each other. Happy birthday!',
      'Friends like you make life better. Hope your birthday is as great as you are.',
      'Happy birthday! Thanks for always being in my corner. Let us celebrate you today.',
      'Lucky is not a strong enough word for how I feel to have you as a friend. Happy birthday.',
    ],
  },
  {
    heading: 'For a milestone birthday',
    intro:   'Big birthdays like 30, 40, 50, and beyond deserve a message that honors the moment.',
    messages: [
      'Happy birthday! Here is to a milestone well earned and many more wonderful years ahead.',
      'A big birthday calls for big celebration. You have earned every bit of it. Cheers to you!',
      'Look how far you have come. Wishing you a milestone birthday filled with pride and joy.',
      'The best chapters are still ahead. Happy milestone birthday!',
      'Today we celebrate not just a birthday but a life well lived. Congratulations and happy birthday.',
    ],
  },
];

const FAQ = [
  {
    q: 'What should I write in a birthday card?',
    a: 'Start with a warm greeting like "Happy birthday," add a personal line about the person or your relationship, then close with a wish for the year ahead and your name. Keep it genuine: a short, sincere note means more than a long, generic one. If you are stuck, pick a message from this guide that matches your tone and relationship, then change a word or two to make it yours.',
  },
  {
    q: 'What is a good short birthday message?',
    a: 'A good short birthday message is warm and specific, for example: "Happy birthday! Hope your day is as special as you are." or "Wishing you a wonderful birthday and a year full of good things." Short messages work well when you want something simple and sincere.',
  },
  {
    q: 'How do I write a funny birthday message without being mean?',
    a: 'Keep the joke about lighthearted things like cake, age, or shared memories, and always land on a kind note. For example: "Happy birthday! You are not getting older, you are just becoming a classic." Aim for a smile, not a sting.',
  },
  {
    q: 'How do I sign off a birthday card?',
    a: 'Match the sign-off to your relationship. "Love," works for family and close friends, "With love" or "All my love" for a partner, and "Best wishes," "Warmly," or "Cheers," for friends and colleagues. Then add your name.',
  },
];

export default function WhatToWriteBirthdayPage() {
  const canonical = `${BASE_URL}${PATH}`;

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'What to Write in a Birthday Card: 35+ Message Ideas',
      description: 'Ready-to-use birthday card messages grouped by tone and relationship, from heartfelt and sentimental to short and funny, plus tips on what to write and how to sign off.',
      author: { '@type': 'Organization', name: 'MomentSent' },
      publisher: {
        '@type': 'Organization',
        name: 'MomentSent',
        logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo-wordmark.png` },
      },
      mainEntityOfPage: canonical,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'What to Write in a Birthday Card', item: canonical },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <PageSEO
        title="What to Write in a Birthday Card: 35+ Message Ideas | MomentSent"
        description="Stuck on what to write in a birthday card? Use these ready-to-go birthday messages grouped by tone and relationship, from heartfelt to funny, plus tips on what to say and how to sign off."
        canonical={canonical}
        structuredData={structuredData}
      />
      <LandingNav />

      {/* ── Hero / answer-first intro ───────────────────────────────────────── */}
      <section className="pt-28 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-body text-sm text-sage font-semibold tracking-widest uppercase mb-4">
            Message ideas
          </p>
          <h1 className="font-heading text-4xl md:text-5xl text-navy leading-tight mb-6">
            What to write in a birthday card
          </h1>
          <p className="font-body text-lg text-navy/80 leading-relaxed">
            The best birthday card messages are short, sincere, and personal. Start with a warm
            greeting, add one specific line about the person or your relationship, and close with a
            wish for the year ahead. Below are more than 35 ready-to-use messages grouped by tone
            and relationship. Pick one that fits, change a word or two to make it yours, and you are
            done.
          </p>
        </div>
      </section>

      {/* ── Message groups ──────────────────────────────────────────────────── */}
      <section className="pb-4 px-6">
        <div className="max-w-3xl mx-auto space-y-10">
          {GROUPS.map((g) => (
            <div key={g.heading}>
              <h2 className="font-heading text-2xl text-navy mb-2">{g.heading}</h2>
              <p className="font-body text-base text-gray-500 leading-relaxed mb-5">{g.intro}</p>
              <ul className="space-y-3">
                {g.messages.map((m) => (
                  <li
                    key={m}
                    className="bg-white rounded-card p-5 shadow-warm border border-gray-100
                               font-body text-base text-gray-700 leading-relaxed"
                  >
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ── Tips on writing the card ────────────────────────────────────────── */}
      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl text-navy mb-6">Tips for writing a birthday card</h2>
          <ul className="space-y-4">
            {[
              'Open with a clear greeting. "Happy birthday" never goes out of style, and it sets the tone right away.',
              'Add one specific detail. A single line about a shared memory, a quality you admire, or your hopes for their year makes the card feel personal.',
              'Match the tone to the person. Heartfelt for family, playful for close friends, warm and simple for everyone else.',
              'Keep it genuine over long. A few sincere words mean more than a full page of filler.',
              'Sign off with the right closer. "Love" for family, "All my love" for a partner, "Cheers" or "Best wishes" for friends, then your name.',
            ].map((t) => (
              <li key={t} className="flex gap-3">
                <span className="text-sage font-bold mt-0.5" aria-hidden="true">✓</span>
                <span className="font-body text-base text-gray-600 leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl text-navy mb-6">Frequently asked questions</h2>
          <div className="space-y-6">
            {FAQ.map((f) => (
              <div key={f.q}>
                <h3 className="font-heading text-lg text-navy mb-2">{f.q}</h3>
                <p className="font-body text-base text-gray-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA back to the birthday product ────────────────────────────────── */}
      <section className="py-20 px-6 bg-sage/10 mt-auto">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-navy mb-4">
            Found the perfect words? Let us mail the card.
          </h2>
          <p className="font-body text-base text-gray-500 leading-relaxed mb-8">
            With <Link to="/birthday-cards" className="text-sage font-semibold hover:underline">automatic birthday cards</Link> from
            MomentSent, you add the birthday once and we mail a real, physical card every year, right
            on time, with your message inside.
          </p>
          <Link to="/signup" className="btn-primary inline-block px-8 py-3 text-base">
            Start your free trial →
          </Link>
          <p className="mt-3 font-body text-xs text-gray-400">
            14-day free trial. No credit card required.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
